/**
 * Robust connectivity probe.
 *
 * `navigator.onLine` returns true any time the device is connected to *any*
 * network (e.g. a WiFi router that has lost its internet uplink). For a POS
 * we need to actually verify the API is reachable, so this module:
 *   - Subscribes to native online/offline events for immediate flips.
 *   - Runs a lightweight backend probe every 15s, plus on focus/online events.
 *   - Notifies subscribers when the effective online status changes.
 */

const API = process.env.REACT_APP_BACKEND_URL;
const PROBE_URL = `${API}/api/health`;
const PROBE_INTERVAL_MS = 15000;
const PROBE_TIMEOUT_MS = 8000;
// One dropped probe (Kubernetes ingress hiccup, GC pause, roaming WiFi
// hand-off) should NOT flip the till to Offline. Require several
// consecutive failures — matches the OfflineIndicator widget's own
// hysteresis. The single-fail flip was why "Offline" was showing most of
// the time even though the backend was fine.
const CONSECUTIVE_FAILS_TO_GO_OFFLINE = 3;

let effectiveOnline = typeof navigator !== "undefined" ? navigator.onLine : true;
let consecutiveFails = 0;
let probeTimer = null;
const subscribers = new Set();

function notify() {
  for (const fn of subscribers) {
    try { fn(effectiveOnline); } catch (e) { /* ignore subscriber errors */ }
  }
}

async function probe() {
  // If the OS itself says offline, there's no point probing.
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    consecutiveFails = CONSECUTIVE_FAILS_TO_GO_OFFLINE;
    if (effectiveOnline) { effectiveOnline = false; notify(); }
    return;
  }
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), PROBE_TIMEOUT_MS);
    const res = await fetch(PROBE_URL, {
      method: "GET",
      cache: "no-store",
      signal: ctrl.signal,
    });
    clearTimeout(t);
    if (res.ok) {
      consecutiveFails = 0;
      if (!effectiveOnline) {
        effectiveOnline = true;
        notify();
      }
      return;
    }
    consecutiveFails += 1;
  } catch (e) {
    consecutiveFails += 1;
  }
  if (consecutiveFails >= CONSECUTIVE_FAILS_TO_GO_OFFLINE && effectiveOnline) {
    effectiveOnline = false;
    notify();
  }
}

function startProbeLoop() {
  if (probeTimer) return;
  // Kick off an immediate probe, then schedule the loop.
  probe();
  probeTimer = setInterval(probe, PROBE_INTERVAL_MS);
}

if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    // Native says online — verify with a probe.
    probe();
  });
  window.addEventListener("offline", () => {
    if (effectiveOnline) {
      effectiveOnline = false;
      notify();
    }
  });
  window.addEventListener("focus", () => probe());
  startProbeLoop();
}

export function isOnline() {
  return effectiveOnline;
}

/**
 * Subscribe to effective-online status changes.
 * Returns an unsubscribe function.
 */
export function subscribeOnline(fn) {
  subscribers.add(fn);
  // Push the current state immediately so the subscriber doesn't have to wait.
  try { fn(effectiveOnline); } catch (e) { /* ignore */ }
  return () => subscribers.delete(fn);
}

/**
 * Force a probe now (useful right before placing a sale).
 */
export function probeNow() {
  return probe();
}
